import { useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireBusiness } from '@/components/RequireBusiness';
import { useApp } from '@/contexts/AppContext';
import { AddCategoryModal } from '@/components/AddCategoryModal';
import { EditCategoryModal } from '@/components/EditCategoryModal';
import { ReviewCategory } from '@/hooks/useCategories';
import { Badge } from '@/components/ui/badge';

export default function CategoriesPage() {
  return (
    <RequireBusiness>
      <CategoriesContent />
    </RequireBusiness>
  );
}

function CategoriesContent() {
  const { categories, deleteCategory } = useApp();
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editCategory, setEditCategory] = useState<ReviewCategory | null>(null);

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto w-full">
        {/* Header - Mobile Optimized */}
        <div className="flex flex-col gap-3 mb-6">
          <div>
            <h1 className="section-title">Categories</h1>
            <p className="section-subtitle mt-1 text-sm">
              Personalize reviews with keywords & USP
            </p>
          </div>
          <Button 
            onClick={() => setShowAddCategory(true)}
            className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Plus className="mr-2" size={18} />
            Add Category
          </Button>
        </div>

        {/* Categories Grid - Mobile Optimized */}
        <div className="grid gap-4">
          {categories.map((category) => (
            <div key={category.id} className="soft-card p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-base sm:text-lg truncate">{category.name}</h3>
                  {category.description && (
                    <p className="text-muted-foreground text-sm mt-0.5 line-clamp-2">{category.description}</p>
                  )}
                </div>
                <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                  <button
                    onClick={() => setEditCategory(category)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                    aria-label="Edit category"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => deleteCategory(category.id)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-destructive"
                    aria-label="Delete category"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="space-y-2.5 text-sm">
                {/* Keywords */}
                <div>
                  <span className="text-muted-foreground font-medium text-xs uppercase tracking-wide">Keywords</span>
                  {category.keywords && category.keywords.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {category.keywords.slice(0, 5).map((keyword, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {keyword}
                        </Badge>
                      ))}
                      {category.keywords.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{category.keywords.length - 5} more
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted-foreground italic text-xs mt-1">Not set</p>
                  )}
                </div>

                {/* USP & Location - Compact on Mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <span className="text-muted-foreground font-medium text-xs uppercase tracking-wide">USP</span>
                    {category.usp ? (
                      <p className="text-sm mt-0.5 line-clamp-1">{category.usp}</p>
                    ) : (
                      <p className="text-muted-foreground italic text-xs mt-0.5">Not set</p>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground font-medium text-xs uppercase tracking-wide">Location</span>
                    {category.location ? (
                      <p className="text-sm mt-0.5 line-clamp-1">{category.location}</p>
                    ) : (
                      <p className="text-muted-foreground italic text-xs mt-0.5">Not set</p>
                    )}
                  </div>
                </div>

                {/* Created date */}
                <div className="pt-2 border-t border-border">
                  <span className="text-muted-foreground text-xs">
                    Created {formatDate(category.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {categories.length === 0 && (
          <div className="soft-card py-12 text-center">
            <p className="text-muted-foreground">No categories yet</p>
            <Button 
              onClick={() => setShowAddCategory(true)}
              variant="outline"
              className="mt-4"
            >
              Add your first category
            </Button>
          </div>
        )}
      </div>

      <AddCategoryModal 
        open={showAddCategory} 
        onOpenChange={setShowAddCategory} 
      />
      
      <EditCategoryModal
        open={!!editCategory}
        onOpenChange={(open) => !open && setEditCategory(null)}
        category={editCategory}
      />
    </AppLayout>
  );
}
